import * as THREE from 'three';

/**
 * CG の部屋に貼る質感。画像ファイルを持たず、キャンバスに描いて作る。
 * 1枚が 1m × 1m に相当し、材質を取るときに面の大きさぶん繰り返す。
 *
 * material(name, w, h) で「w m × h m の面用の材質」を返す。
 * 同じ組み合わせは使い回す（部屋の数だけ材質を作ると重いため）。
 */
const SIZE = 512;
const textures = new Map();
const materials = new Map();

const PAINTERS = {
  oak: (c) => wood(c, '#d9b98b', '#8a6a44', 78, 0.10),
  oakLight: (c) => wood(c, '#e3c7a0', '#9a7a55', 78, 0.08),
  walnut: (c) => wood(c, '#8a6040', '#3f2a18', 70, 0.14),
  cedar: (c) => wood(c, '#d9ae74', '#8a5f34', 96, 0.10),
  woodCeiling: (c) => wood(c, '#d7bd96', '#a58559', 120, 0.06, true),
  tatami: (c) => tatami(c),
  plaster: (c) => noise(c, '#efe6d6', 10),
  plasterWarm: (c) => noise(c, '#f1e3c9', 10),
  plasterGrey: (c) => noise(c, '#8d8b88', 12),
  felt: (c) => noise(c, '#5f6166', 16),
  tileBeige: (c) => tiles(c, '#d9cbb3', '#b8a88e', 2),
  tileLight: (c) => tiles(c, '#e6e0d6', '#c8c0b4', 3),
  tileGrey: (c) => tiles(c, '#8d8b86', '#6d6b66', 3),
  tileDark: (c) => tiles(c, '#3f3f3d', '#2a2a29', 3),
  stone: (c) => tiles(c, '#cfc3ad', '#a99d88', 1.6),
  shoji: (c) => shoji(c),
  fusuma: (c) => fusuma(c),
  slats: (c) => slats(c),
  lattice: (c) => lattice(c),
  roof: (c) => noise(c, '#4a4340', 8),
  linen: (c) => noise(c, '#efe9dd', 6),
  blanket: (c) => noise(c, '#c9bfae', 6),
};

/** 単色の材質（家具など） */
export function plain(hex, options = {}) {
  const key = `plain:${hex}:${JSON.stringify(options)}`;
  if (!materials.has(key)) materials.set(key, new THREE.MeshLambertMaterial({ color: hex, ...options }));
  return materials.get(key);
}

/** 光る材質（照明・障子越しの明かり） */
export function glow(hex, intensity = 1) {
  const key = `glow:${hex}:${intensity}`;
  if (!materials.has(key)) {
    materials.set(key, new THREE.MeshBasicMaterial({ color: new THREE.Color(hex).multiplyScalar(intensity) }));
  }
  return materials.get(key);
}

/** ガラス */
export function glass() {
  return plain(0xd6e6ee, { transparent: true, opacity: 0.22, depthWrite: false });
}

/** 名前の質感を w m × h m の面に貼る材質 */
export function material(name, w = 1, h = 1) {
  if (!(name in PAINTERS)) throw new Error(`質感 ${name} はありません`);
  const rx = Math.max(1, Math.round(w * 4)) / 4;
  const ry = Math.max(1, Math.round(h * 4)) / 4;
  const key = `${name}:${rx}:${ry}`;
  if (materials.has(key)) return materials.get(key);

  const base = texture(name);
  const map = base.clone();
  map.repeat.set(rx, ry);
  map.needsUpdate = true;
  const options = { map };
  if (name === 'shoji') {
    // 障子は外の光で明るく見せる
    materials.set(key, new THREE.MeshBasicMaterial({ ...options, side: THREE.DoubleSide }));
  } else {
    materials.set(key, new THREE.MeshLambertMaterial(options));
  }
  return materials.get(key);
}

function texture(name) {
  if (textures.has(name)) return textures.get(name);
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const context = canvas.getContext('2d');
  PAINTERS[name](context);
  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  textures.set(name, tex);
  return tex;
}

// ---------- 描き方 ----------

let seed = 12345;
function rand() {
  seed = (seed * 1664525 + 1013904223) % 4294967296;
  return seed / 4294967296;
}

function shade(hex, delta) {
  const n = parseInt(hex.slice(1), 16);
  const ch = (s) => Math.max(0, Math.min(255, ((n >> s) & 255) + delta));
  return `rgb(${ch(16)},${ch(8)},${ch(0)})`;
}

function rgba(hex, a) {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}

/** 板張り。plank は板の幅(px)。vertical で板を縦に */
function wood(c, base, dark, plank, grainAlpha, vertical = false) {
  seed = 777;
  c.save();
  if (vertical) {
    c.translate(SIZE, 0);
    c.rotate(Math.PI / 2);
  }
  c.fillStyle = base;
  c.fillRect(0, 0, SIZE, SIZE);
  for (let p = 0; p * plank < SIZE; p += 1) {
    const y0 = p * plank;
    c.fillStyle = shade(base, Math.round((rand() - 0.5) * 26));
    c.fillRect(0, y0, SIZE, plank);
    for (let i = 0; i < 16; i += 1) {
      c.strokeStyle = rgba(dark, grainAlpha * (0.5 + rand()));
      c.lineWidth = 0.8 + rand() * 1.6;
      c.beginPath();
      const y = y0 + rand() * plank;
      const wobble = 2 + rand() * 4;
      const freq = 1 + rand() * 3;
      c.moveTo(0, y);
      for (let x = 0; x <= SIZE; x += 16) c.lineTo(x, y + Math.sin((x / SIZE) * Math.PI * freq) * wobble);
      c.stroke();
    }
    c.fillStyle = rgba(dark, 0.45);
    c.fillRect(0, y0, SIZE, 2);
    const ex = (p * 197 + 60) % SIZE;
    c.fillRect(ex, y0, 2, plank);
  }
  c.restore();
}

/** 縁なし畳。細い横の織り目と、畳1枚(0.9m × 1.8m)ごとの継ぎ目 */
function tatami(c) {
  c.fillStyle = '#d3cb9e';
  c.fillRect(0, 0, SIZE, SIZE);
  for (let y = 0; y < SIZE; y += 3) {
    c.fillStyle = y % 6 === 0 ? 'rgba(120,110,60,0.18)' : 'rgba(255,250,220,0.15)';
    c.fillRect(0, y, SIZE, 1);
  }
  c.fillStyle = 'rgba(90,80,40,0.35)';
  const half = Math.round(SIZE * 0.9);
  c.fillRect(half - 1, 0, 2, SIZE);
  c.fillRect(0, half - 1, SIZE, 2);
}

/** 漆喰・クロス。細かいムラ */
function noise(c, base, amount) {
  seed = 4242;
  c.fillStyle = base;
  c.fillRect(0, 0, SIZE, SIZE);
  for (let i = 0; i < 9000; i += 1) {
    c.fillStyle = shade(base, Math.round((rand() - 0.5) * amount * 2));
    c.fillRect(rand() * SIZE, rand() * SIZE, 2 + rand() * 6, 2 + rand() * 6);
  }
}

/** タイル。perMeter = 1m に何枚か */
function tiles(c, base, grout, perMeter) {
  seed = 99;
  c.fillStyle = grout;
  c.fillRect(0, 0, SIZE, SIZE);
  const t = SIZE / perMeter;
  for (let y = 0; y < perMeter; y += 1) {
    for (let x = 0; x < perMeter; x += 1) {
      c.fillStyle = shade(base, Math.round((rand() - 0.5) * 16));
      c.fillRect(x * t + 2, y * t + 2, t - 4, t - 4);
    }
  }
}

/** 障子。白い紙に木の桟 */
function shoji(c) {
  c.fillStyle = '#f7f2e8';
  c.fillRect(0, 0, SIZE, SIZE);
  c.fillStyle = '#7a5433';
  const n = 5;
  for (let i = 0; i <= n; i += 1) {
    const p = Math.round((SIZE / n) * i);
    c.fillRect(p - 3, 0, 6, SIZE);
    c.fillRect(0, p - 3, SIZE, 6);
  }
}

/** 襖。生成りの紙に、腰の帯と引手 */
function fusuma(c) {
  noise(c, '#f1e9d8', 6);
  c.fillStyle = '#6b4a2b';
  c.fillRect(0, 0, 4, SIZE);
  c.fillRect(SIZE - 4, 0, 4, SIZE);
  c.fillStyle = '#1d1d1d';
  c.beginPath();
  c.arc(SIZE * 0.9, SIZE * 0.5, 9, 0, Math.PI * 2);
  c.fill();
}

/** 濃い色の縦格子（ラウンジの壁） */
function slats(c) {
  c.fillStyle = '#222222';
  c.fillRect(0, 0, SIZE, SIZE);
  const w = SIZE / 12;
  for (let i = 0; i < 12; i += 1) {
    c.fillStyle = i % 2 === 0 ? '#3a3a3a' : '#2c2c2c';
    c.fillRect(i * w + 3, 0, w - 6, SIZE);
  }
}

/** 玄関の格子窓 */
function lattice(c) {
  c.fillStyle = '#f3ecdf';
  c.fillRect(0, 0, SIZE, SIZE);
  c.fillStyle = '#6b4a2b';
  const n = 8;
  for (let i = 0; i <= n; i += 1) {
    const p = Math.round((SIZE / n) * i);
    c.fillRect(p - 4, 0, 8, SIZE);
    c.fillRect(0, p - 4, SIZE, 8);
  }
}
